export const LEGAL_DOCS = {
  terms: {
    path: "/terms",
    metaTitle: "Terms of Service | Sempresera",
    metaDescription:
      "Terms of Service for Sempresera's managed IT services — service scope, payment, limitations of liability, dispute resolution and governing law.",
    eyebrow: "Legal",
    h1: "Terms of Service",
    updated: "Last updated: July 2026",
    intro:
      "These Terms of Service govern your use of the Sempresera website and any managed IT services provided by Sempresera. By using this website or engaging our services, you agree to these terms.",
    sections: [
      {
        h: "1. Services",
        p: [
          "Sempresera provides managed IT services including monitoring, cybersecurity, cloud administration, help desk support, and backup and disaster recovery (the \"Services\"). The specific scope, pricing and service levels for any client engagement are defined in a written service agreement between Sempresera and the client. Where a service agreement exists, it controls over anything on this website.",
          "Website content — including service descriptions, response targets and pricing illustrations — is general information, not an offer, and may change without notice. Estimates produced by the pricing calculator are illustrative only and do not constitute a quote or contract.",
        ],
      },
      {
        h: "2. No guarantee of outcomes",
        p: [
          "Technology environments carry inherent risk. While Sempresera applies industry-recognized tools and practices — including 24/7 monitoring and human-led security operations — no provider can guarantee that systems will be uninterrupted, error-free, or free from security incidents. Sempresera does not warrant that the Services will prevent every cyberattack, data loss event, or system failure.",
          "Compliance support (including HIPAA-related support) is assistance with technical and administrative safeguards; it is not legal advice and does not by itself make any organization compliant with any law or regulation.",
        ],
      },
      {
        h: "3. Client responsibilities",
        p: [
          "Clients are responsible for maintaining accurate account information, following reasonable security guidance (including multi-factor authentication), ensuring they have rights to all data and software they ask us to manage, and maintaining any hardware, software or subscriptions outside the agreed scope of Services.",
          "Clients remain responsible for their own business decisions, regulatory obligations, and for the acts of their own employees and contractors.",
        ],
      },
      {
        h: "4. Third-party products and services",
        p: [
          "The Services incorporate third-party platforms and vendors (for example Huntress, MSP360 and Microsoft 365). Sempresera is not responsible for the acts, omissions, outages, vulnerabilities or changes of third-party providers, and your use of those platforms may be subject to their own terms and license agreements.",
        ],
      },
      {
        h: "5. Payment",
        p: [
          "Fees, billing timing and any volume-based adjustments are set out in the client's service agreement or invoice. Unless otherwise agreed in writing, invoices are due on receipt and late payments may pause non-critical work until the account is current.",
        ],
      },
      {
        h: "6. Disclaimer of warranties",
        p: [
          "TO THE MAXIMUM EXTENT PERMITTED BY LAW, THE WEBSITE AND SERVICES ARE PROVIDED \"AS IS\" AND \"AS AVAILABLE.\" SEMPRESERA DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.",
        ],
      },
      {
        h: "7. Limitation of liability",
        p: [
          "TO THE MAXIMUM EXTENT PERMITTED BY LAW, SEMPRESERA WILL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL OR PUNITIVE DAMAGES, OR FOR LOST PROFITS, LOST REVENUE, LOST DATA, OR BUSINESS INTERRUPTION, ARISING OUT OF OR RELATED TO THE SERVICES OR THIS WEBSITE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.",
          "SEMPRESERA'S TOTAL AGGREGATE LIABILITY FOR ANY CLAIM ARISING FROM THE SERVICES OR THESE TERMS WILL NOT EXCEED THE AMOUNTS PAID BY THE CLIENT TO SEMPRESERA FOR THE SERVICES IN THE TWELVE (12) MONTHS PRECEDING THE EVENT GIVING RISE TO THE CLAIM.",
        ],
      },
      {
        h: "8. Indemnification",
        p: [
          "You agree to indemnify and hold harmless Sempresera, its officers and employees from claims, damages and expenses (including reasonable attorneys' fees) arising from your misuse of the Services, your violation of these Terms, your violation of any law or third-party right, or content and data you provide to us.",
        ],
      },
      {
        h: "9. Intellectual property",
        p: [
          "All content on this website — text, design, logos and graphics — is the property of Sempresera or its licensors and may not be copied or reused without written permission, except for normal browsing and sharing of links.",
        ],
      },
      {
        h: "10. Dispute resolution and governing law",
        p: [
          "These Terms are governed by the laws of the State of Rhode Island, without regard to conflict-of-law rules. Any dispute arising from these Terms or the Services will be resolved by binding arbitration in Providence County, Rhode Island, on an individual basis, and each party waives the right to a jury trial and to participate in a class action, to the extent permitted by law.",
          "If arbitration is found unenforceable as to a claim, the state and federal courts located in Rhode Island will have exclusive jurisdiction.",
        ],
      },
      {
        h: "11. General",
        p: [
          "Sempresera is not liable for delays or failures caused by events beyond reasonable control (force majeure), including utility outages, carrier failures, and acts of nature. If any provision is found unenforceable, the remainder stays in effect. These Terms may be updated from time to time; the current version is always posted on this page.",
        ],
      },
      {
        h: "12. Contact",
        p: [
          "Questions about these Terms: Sempresera, 1643 Warwick Ave, Suite 183, Warwick, RI 02889, or email support@sempresera.com.",
        ],
      },
    ],
  },
  privacy: {
    path: "/privacy",
    metaTitle: "Privacy Policy | Sempresera",
    metaDescription:
      "How Sempresera collects, uses and protects personal information submitted through its website — contact details, analytics, retention and your rights.",
    eyebrow: "Legal",
    h1: "Privacy Policy",
    updated: "Last updated: July 2026",
    intro:
      "This Privacy Policy explains what information Sempresera collects through this website, how we use it, and the choices you have. We collect the minimum needed to respond to you and run the site.",
    sections: [
      {
        h: "1. Information we collect",
        p: [
          "Information you give us directly: when you submit the contact form or request a consultation, we collect your name, company, email address, phone number (if provided), your message, and your preferred consultation date and time.",
          "Information collected automatically: we use privacy-respecting analytics (PostHog) to understand how the site is used — pages visited, approximate region, device and browser type. This may involve cookies or similar technologies. We do not sell personal information and we do not use advertising trackers.",
        ],
      },
      {
        h: "2. How we use information",
        p: [
          "We use submitted information to respond to enquiries, schedule consultations, provide and improve our services, and maintain the security of the website. We may send service-related messages in response to your request; we do not add you to marketing lists without your consent.",
        ],
      },
      {
        h: "3. How information is shared",
        p: [
          "We share information only with service providers who help us operate — for example, our email delivery provider, which processes enquiry notifications — and only to the extent needed for that purpose. We do not sell, rent or trade personal information. We may disclose information if required by law or to protect our rights, safety or property.",
        ],
      },
      {
        h: "4. Data retention and security",
        p: [
          "Enquiry records are retained while they are operationally relevant and are then deleted or archived. We apply administrative and technical safeguards appropriate to a managed security provider, but no method of transmission or storage is completely secure, and we cannot guarantee absolute security.",
        ],
      },
      {
        h: "5. Your rights and choices",
        p: [
          "You may request access to, correction of, or deletion of your personal information at any time by emailing support@sempresera.com. Depending on your state of residence, you may have additional statutory rights (for example under state privacy laws); we honor verifiable requests regardless of residency.",
          "Most browsers let you refuse or delete cookies; the website remains usable with cookies disabled.",
        ],
      },
      {
        h: "6. Children",
        p: [
          "This website and our services are directed to businesses and are not intended for children under 13. We do not knowingly collect personal information from children.",
        ],
      },
      {
        h: "7. Changes to this policy",
        p: [
          "We may update this Privacy Policy from time to time. The current version is always posted on this page with its revision date.",
        ],
      },
      {
        h: "8. Contact",
        p: [
          "Privacy questions or requests: Sempresera, 1643 Warwick Ave, Suite 183, Warwick, RI 02889, or email support@sempresera.com.",
        ],
      },
    ],
  },
};
